/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action, computed } from '@ember/object';
import { notEmpty } from '@ember/object/computed';
import Component from '@glimmer/component';
import { assert } from '@ember/debug';
import { isEmpty } from '@ember/utils';
import { run } from '@ember/runloop';
import { guidFor } from '@ember/object/internals';
import { tracked } from '@glimmer/tracking';
import { UploadFile } from 'ember-file-upload';
import { DataTransferWrapper } from 'ember-file-upload/internal';
import isDestroyed from 'cio/utils/is-destroyed';
import joinWords from 'cio/utils/join-words';

interface CioFileInputArgs {
  accept?: string;
  disabled?: boolean;
  dropToAdd?: boolean;
  errors?: (Record<string, any> | string)[];
  label?: string;
  multiple?: boolean;
  name: string;
  pending?: boolean;
  showErrorMessage?: boolean;
  selectedImageUrl?: string;
  onFileDeleted?: () => unknown;
  onFileSelected: (file: UploadFile, resetInput: () => unknown) => unknown;
  onChangeStart: (type: 'replace' | 'delete') => unknown;
  buttonClasses?: string;
  dropzoneClasses?: string;
}

const MIME_TYPE_TO_EXTENSION = {
  'text/csv': 'csv',
  'application/json': 'json',
};

export default class CioFileInput extends Component<CioFileInputArgs> {
  container?: HTMLDivElement;

  readonly guid = guidFor(this);

  // Annoying hack—ember-file-upload >= 6 requires custom validation
  // Unfortunately, when dragging a file(s) over the dropzone, it will
  // only validate after the file has been dropped, not when the file
  // enters. Thus, we need to validate the file using the `validateFile`
  // method manually when the user drags a file into the dropzone.
  // Further, ember-file-upload no longer provides a `dropzone.valid`
  // property in the dropzone object, so we need to track it ourselves.
  @tracked draggedFileInvalid = false;

  get accept() {
    return this.args.accept ?? '*';
  }

  get label() {
    return this.args.label ?? 'Choose file';
  }

  get showErrorMessage() {
    return this.args.showErrorMessage ?? true;
  }

  get onFileDeleted() {
    return this.args.onFileDeleted ?? (() => {});
  }

  get acceptedFileTypes() {
    if (this.accept === '*') {
      return 'any file';
    }

    return joinWords(
      this.accept
        .split(',')
        .map((mime) => (mime === 'text/csv' || mime === 'application/json' ? MIME_TYPE_TO_EXTENSION[mime] : null))
        .filter((ext: string): ext is string => !!ext),
    );
  }

  @notEmpty('args.errors') hasError!: boolean;

  @computed('args.errors.[]')
  get errorMessage() {
    if (isEmpty(this.args.errors)) {
      return [];
    }

    return this.args
      .errors!.map((error) => {
        assert(
          'errors must be objects with property "message", "detail" or strings',
          typeof error === 'string' || typeof error.message === 'string' || typeof error.detail === 'string',
        );

        return typeof error === 'string' ? error : error.message || error.detail;
      })
      .join(' ');
  }

  @action
  setContainerElement(el: HTMLDivElement) {
    this.container = el;
  }

  @action
  resetInput() {
    if (!isDestroyed(this)) {
      run(() => {
        let input = this.container?.querySelector<HTMLInputElement>('input[type="file"]');

        if (input) {
          input.value = '';
        }
      });
    }
  }

  @action
  onFileSelectedAction({ file }: { file: UploadFile }) {
    this.args.onFileSelected(file, this.resetInput);
  }

  @action
  validateFile(file: File | DataTransferItem) {
    if (this.args.accept === '*' || this.args.accept == null) {
      return true;
    }

    return this.args.accept.includes(file.type);
  }

  // Due to a bug or race condition, `_files` here is always an empty
  // array. The DataTransfer object also has an empty files array.
  // Instead, we'll loop through all the items in the DataTransfer object
  // and then validate each object individually.
  @action
  onDragEnter(_files: File[], dataTransferWrapper: DataTransferWrapper) {
    this.draggedFileInvalid = !dataTransferWrapper.items.every((f) => this.validateFile(f));
  }
}
